/**
 * keyboardGuard.ts — the webapp's FIRST single-key shortcut guard (Log
 * Activity slice C2, RULED 2026-10-09: Q1 bare L, Q3 no modifier at all,
 * Q9 one bubble-phase keydown listener on document). Pure DOM, no React.
 *
 * A bare letter is a key the user may be TYPING, so every check here asks
 * "is this keystroke free?" and answers no on any doubt:
 *   - the target is, or sits inside, a field: input, textarea, select, an
 *     editable region, or a textbox / combobox / listbox / searchbox role
 *     (the pickers' roles — a key inside a list is the list's);
 *   - any modifier is down, Shift included (Shift+L is a different key;
 *     caps lock is not a modifier, so a capital L with no Shift still counts);
 *   - the event is spent: a held key repeating, an IME composition (or its
 *     229 keyCode), or a handler that already called preventDefault;
 *   - anything modal is open — every dialog this app shows carries
 *     aria-modal="true", so one selector covers the sheet, its discard
 *     confirm, the deal modals and the contact drawer;
 *   - the target sits inside an open shell MENU (Sidebar v2 S1: the
 *     department switcher, the account menu) — a popover, never aria-modal,
 *     that keeps focus on an item inside it while open, so the key is the
 *     menu's.
 * The host decides the rest (its switch, its own open sheet) BEFORE asking.
 */

/** Where a keystroke is text (or a menu's), not a command. `closest` covers "inside". */
export const TYPING_SELECTOR =
  'input, textarea, select, [contenteditable=""], [contenteditable="true"], [contenteditable="plaintext-only"], ' +
  '[role="textbox"], [role="combobox"], [role="listbox"], [role="searchbox"], [role="menu"]'

export const MODAL_SELECTOR = '[aria-modal="true"]'

type KeyLike = Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey'>
type SpentLike = Pick<KeyboardEvent, 'repeat' | 'isComposing' | 'keyCode' | 'defaultPrevented'>

export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  if (target instanceof HTMLElement && target.isContentEditable) return true
  return target.closest(TYPING_SELECTOR) !== null
}

/** Any modifier, Shift included (Q3). */
export function hasModifier(e: Pick<KeyLike, 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey'>): boolean {
  return e.metaKey || e.ctrlKey || e.altKey || e.shiftKey
}

/** A keystroke that is not a fresh, unclaimed press. */
export function isSpentEvent(e: SpentLike): boolean {
  return e.repeat || e.isComposing || e.keyCode === 229 || e.defaultPrevented
}

/** The bare key, by `key` and never by `code` (Q3): the character the
 *  layout printed, lower-cased so caps lock reads the same as no caps lock. */
export function isBareKey(e: KeyLike, key: string): boolean {
  return e.key.toLowerCase() === key && !hasModifier(e)
}

export function isModalOpen(doc: Document = document): boolean {
  return doc.querySelector(MODAL_SELECTOR) !== null
}

/** The whole guard: true only when `key` was pressed bare, fresh, outside
 *  any field, with nothing modal open. */
export function shortcutEligible(e: KeyboardEvent, key: string, doc: Document = document): boolean {
  return isBareKey(e, key) && !isSpentEvent(e) && !isTypingTarget(e.target) && !isModalOpen(doc)
}

// ── The sidebar chord (Sidebar v2 slice S2, RULED 2026-10-11 (6)) ───────────
// ⌘\ or Ctrl+\ toggles the rail. A CHORD, not a single key, so the typing
// guard does not apply (it types nothing, and it toggles from inside a field
// or an open shell menu — the toggle closes the menu) and the Settings
// single-key switch is not its business (WCAG 2.1.4 covers single keys).
// Exactly ONE of Meta / Control, no Alt, no Shift; matched by `key`, so a
// layout that needs AltGr for "\" does not fire it (said, not solved).

/** ⌘\ or Ctrl+\ — exactly one of the two, nothing else held. */
export function isSidebarChord(e: KeyLike): boolean {
  return e.key === '\\' && e.metaKey !== e.ctrlKey && !e.altKey && !e.shiftKey
}

// Sidebar v2 S3c (RULED 2026-10-11 (3)): below 768 the sidebar is a modal
// drawer and the chord must close it, so the drawer's OWN modal is exempt —
// and only it: the aside#k-sidebar element itself. Any other aria-modal
// element (a SideSheet, a confirm, a modal nested inside the aside) still
// refuses the chord. The single-key guard above has NO exemption: L stays
// inert while the drawer is open.
export const SIDEBAR_DRAWER_SELF = 'aside#k-sidebar'
const OTHER_MODAL_SELECTOR = `${MODAL_SELECTOR}:not(${SIDEBAR_DRAWER_SELF})`

/** A modal open other than the sidebar drawer itself. */
export function isOtherModalOpen(doc: Document = document): boolean {
  return doc.querySelector(OTHER_MODAL_SELECTOR) !== null
}

/** The chord, fresh, with no dialog open but (at most) the sidebar drawer. */
export function sidebarChordEligible(e: KeyboardEvent, doc: Document = document): boolean {
  return isSidebarChord(e) && !isSpentEvent(e) && !isOtherModalOpen(doc)
}
